// User Image Service - Handles user image uploads, compression, and storage.
// Uploads are compressed client-side to a data URI and posted to the backend, which
// stores bytes in SQLite and serves them via /api/user-images/:token and /api/user-photos/:token.

import { API_BASE } from '../config/api';
import AuthService from './authService';
import { PHOTO_SPECS, SLOW_CONNECTION_PHOTO_SPEC } from '../utils/mediaUploadSpecs';

// exifr is dynamically imported so its ~25 KB stays out of the main bundle.
// `exifrModulePromise` memoizes that import: the FIRST photo upload after a
// fresh page load used to fire two concurrent `import('exifr')` calls (one for
// GPS, one for camera metadata) which both kicked off a cold chunk download and
// then both called `exifr.parse()` on the SAME File at the same time. That
// concurrent cold read was racy and intermittently threw on the first attempt,
// while succeeding on every attempt after (module cached, timing changed) —
// the classic "fails first time, works after re-opening the app" symptom.
// Memoizing the import + parsing each File exactly once removes that race.
let exifrModulePromise = null;
function loadExifr() {
  if (!exifrModulePromise) {
    exifrModulePromise = import('exifr')
      .then((m) => m.default)
      .catch((err) => {
        // Reset so a transient chunk-load failure can be retried on the next
        // upload instead of being cached as a permanent rejection.
        exifrModulePromise = null;
        throw err;
      });
  }
  return exifrModulePromise;
}

// The exact EXIF tags we read on upload — GPS convenience props first, then
// camera/lens/exposure fields. Hoisted to module scope so the ~30-string list
// isn't re-allocated on every photo. Privacy: intentionally excludes
// OwnerName / *SerialNumber and other identifying tags.
const EXIF_PICK_FIELDS = [
  'latitude', 'longitude', 'GPSLatitude', 'GPSLongitude',
  'GPSLatitudeRef', 'GPSLongitudeRef',
  'Make', 'Model', 'LensModel', 'LensInfo', 'LensMake',
  'FocalLength', 'FocalLengthIn35mmFormat',
  'FNumber', 'ApertureValue',
  'ExposureTime', 'ShutterSpeedValue',
  'ISO', 'ISOSpeedRatings',
  'ExposureCompensation', 'ExposureProgram', 'ExposureMode',
  'Flash', 'WhiteBalance', 'MeteringMode',
  'Software', 'DateTimeOriginal', 'CreateDate',
  'ColorSpace', 'PixelXDimension', 'PixelYDimension',
  'Orientation'
];

// EXIF cleanup helpers — defined at module scope so they're not re-allocated
// on every photo upload. EXIF values can be poorly-formed (whitespace, "----"
// placeholders, NaN, sentinel zeros), so cleanString/numericOrNull are the
// trust boundary that turns raw exifr output into something safe to display.
function cleanString(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Common placeholder values cameras write when a field is unavailable.
  if (/^-+$/.test(trimmed) || trimmed === 'Unknown' || trimmed === 'undef') return null;
  return trimmed;
}

function numericOrNull(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

// EXIF Flash is a bitmask in spec, but exifr versions decode it variably:
//   - integer (most common): bit 0 = fired
//   - { fired: bool, ... } object (some image formats)
//   - boolean (rare, post-decode)
// Returning null when the shape is unknown is safer than guessing.
// Format a Date as local-naive "YYYY-MM-DD HH:MM:SS". Used for photo_taken_at
// so the camera's wall time survives upload as-is (no timezone conversion) and
// the string sorts/slices correctly in SQL alongside ISO created_at values.
function formatLocalNaive(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function parseFlashFired(flash) {
  if (flash == null) return null;
  if (typeof flash === 'boolean') return flash;
  if (typeof flash === 'number') return (flash & 1) === 1;
  if (typeof flash === 'object' && typeof flash.fired === 'boolean') return flash.fired;
  return null;
}

// EXIF stores ExposureTime as a decimal in seconds (e.g. 0.004 for 1/250s).
// Photographers expect "1/250" format for fast shutter, "2s" or "1.5s" for slow.
function formatShutterSpeed(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds >= 1) {
    return seconds === Math.floor(seconds) ? `${seconds}s` : `${seconds.toFixed(1)}s`;
  }
  const denominator = Math.round(1 / seconds);
  return `1/${denominator}`;
}

class UserImageService {
  // Compression targets now live in src/utils/mediaUploadSpecs.js, which is
  // also what <MediaQualityHint> renders as user-facing export guidance. They
  // were moved out of this class so the two can't drift: help copy that quotes
  // a limit the compressor no longer enforces is worse than no help copy.
  // Shape is unchanged — compressImage() reads the same keys it always did.
  static IMAGE_CONFIG = PHOTO_SPECS;

  // Fallback config for users on slow connections (3G or worse, or Save-Data
  // header). Trades quality for upload speed automatically — no user-facing
  // toggle. Roughly Instagram-tier output to keep upload times tolerable on
  // cellular without a manual setting.
  static SLOW_CONNECTION_CONFIG = SLOW_CONNECTION_PHOTO_SPEC;

  // Returns true when the browser indicates a slow connection (Save-Data
  // header, or Network Information API reporting 2G/3G effective type).
  // Falls back to false if the API is unavailable (Safari, Firefox).
  static isSlowConnection() {
    if (typeof navigator === 'undefined') return false;
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    if (conn.saveData) return true;
    return conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g' || conn.effectiveType === '3g';
  }

  // How many photos to upload concurrently. 3 keeps a healthy pipeline on
  // normal connections, but on a weak uplink 3 simultaneous multi-MB POSTs
  // starve each other — each crawls toward the 60s client timeout and the
  // whole batch fails photos that would have made it one-at-a-time. Same
  // total transfer either way; serial just lets each upload finish.
  static uploadConcurrency() {
    return this.isSlowConnection() ? 1 : 3;
  }

  static API_IMAGES = `${API_BASE}/api/user-images`;

  /**
   * Extract GPS + camera metadata in a SINGLE pass over the file (before
   * compression strips EXIF).
   *
   * Parses the File exactly once and splits the result into GPS coordinates
   * and camera/lens/exposure metadata. This replaced an earlier pair of
   * methods that each opened and parsed the File independently: running them
   * concurrently (Promise.all) meant two reads of the same File at once, which
   * intermittently failed on the very first upload after a fresh page load
   * (cold exifr import + concurrent read). One parse is both safer and faster.
   *
   * Privacy note: the pick list deliberately omits OwnerName,
   * BodySerialNumber, LensSerialNumber, and other fields that could leak
   * personal data the user didn't intend to share. GPS is split out into its
   * own return field (callers gate sharing on it separately).
   *
   * @param {File} file - Original image file
   * @returns {Promise<{ gps: Object|null, exif: Object|null }>}
   */
  static async extractPhotoMetadata(file) {
    let exifData = null;
    try {
      const exifr = await loadExifr();
      // One parse, union of both field sets. gps:true exposes the
      // latitude/longitude convenience props; the rest are camera metadata.
      exifData = await exifr.parse(file, {
        gps: true,
        tiff: true,
        exif: true,
        ifd1: false,
        interop: false,
        pick: EXIF_PICK_FIELDS
      });
    } catch (error) {
      console.error('Error extracting photo metadata:', error);
      return { gps: null, exif: null, takenAt: null };
    }

    if (!exifData) return { gps: null, exif: null, takenAt: null };

    // --- Capture time, independent of GPS. iOS Safari strips location EXIF
    //     from file-input picks but keeps DateTimeOriginal, so this is the
    //     only capture-time signal for PWA uploads. Formatted as a local-naive
    //     "YYYY-MM-DD HH:MM:SS" string: exifr parses the camera's naive wall
    //     time into a browser-local Date, so reading local components back
    //     reproduces the original wall time regardless of the uploader's
    //     timezone (Date.toString()/toISOString() would not). ---
    const rawTakenAt = exifData.DateTimeOriginal || exifData.CreateDate || null;
    const takenAt = formatLocalNaive(rawTakenAt);

    // --- GPS: explicit hemisphere correction (exifr doesn't always apply the
    //     N/S/E/W reference to the decimal lat/lng convenience props) ---
    let gps = null;
    if (exifData.latitude && exifData.longitude) {
      let lat = exifData.latitude;
      let lng = exifData.longitude;
      const latRef = exifData.GPSLatitudeRef || 'N';
      const lngRef = exifData.GPSLongitudeRef || 'E';
      if (latRef === 'S' && lat > 0) lat = -lat;
      if (lngRef === 'W' && lng > 0) lng = -lng;
      gps = {
        lat,
        lng,
        timestamp: takenAt
      };
    }

    // --- Camera metadata: normalize raw exifr output through the cleaners,
    //     which strip empty strings and sentinel placeholders ("----", etc.) ---
    const cleaned = {
      camera_make: cleanString(exifData.Make),
      camera_model: cleanString(exifData.Model),
      lens_make: cleanString(exifData.LensMake),
      lens_model: cleanString(exifData.LensModel) || cleanString(exifData.LensInfo),
      focal_length: numericOrNull(exifData.FocalLength),
      focal_length_35mm: numericOrNull(exifData.FocalLengthIn35mmFormat),
      aperture: numericOrNull(exifData.FNumber),
      shutter_speed: formatShutterSpeed(exifData.ExposureTime),
      iso: numericOrNull(exifData.ISO ?? exifData.ISOSpeedRatings),
      exposure_compensation: numericOrNull(exifData.ExposureCompensation),
      exposure_program: cleanString(exifData.ExposureProgram),
      exposure_mode: cleanString(exifData.ExposureMode),
      flash_used: parseFlashFired(exifData.Flash),
      white_balance: cleanString(exifData.WhiteBalance),
      metering_mode: cleanString(exifData.MeteringMode),
      software: cleanString(exifData.Software),
      color_space: cleanString(exifData.ColorSpace),
      width_px: numericOrNull(exifData.PixelXDimension),
      height_px: numericOrNull(exifData.PixelYDimension),
      orientation: numericOrNull(exifData.Orientation),
      // Same local-naive format as photo_taken_at — a raw Date here would
      // JSON.stringify to UTC ISO, silently shifted from the wall time.
      taken_at: takenAt
    };
    const filtered = Object.fromEntries(
      Object.entries(cleaned).filter(([, v]) => v != null && v !== '')
    );
    const exif = Object.keys(filtered).length > 0 ? filtered : null;

    return { gps, exif, takenAt };
  }

  // Compress image on client side before upload.
  // Uses createImageBitmap with imageOrientation: 'from-image' so EXIF rotation
  // is applied to pixel data — canvas drawImage(<img>) historically strips this
  // and produced sideways photos on iOS portraits.
  //
  // On slow connections (2G/3G or Save-Data), falls back to a smaller config
  // automatically so uploads stay tolerable on cellular. Profile pics keep
  // their own config (already small enough that downsizing further isn't
  // worth it).
  static async compressImage(file, imageType = 'activity') {
    let config = this.IMAGE_CONFIG[imageType] || this.IMAGE_CONFIG.activity;
    if (imageType !== 'profile' && this.isSlowConnection()) {
      config = this.SLOW_CONNECTION_CONFIG;
    }

    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

    let width = bitmap.width;
    let height = bitmap.height;
    const { maxWidth, maxHeight } = config;
    if (width > height) {
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
    } else if (height > maxHeight) {
      width = Math.round((width * maxHeight) / height);
      height = maxHeight;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const toBlob = (quality) => new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Failed to encode image')),
        config.format,
        quality
      );
    });

    let blob = await toBlob(config.quality);
    let quality = config.quality;
    if (blob.size > config.maxSize) {
      quality = config.quality * 0.8;
      blob = await toBlob(quality);
    }

    return {
      blob,
      // base64 is computed lazily. The upload paths (review photos, trip photos)
      // only send `blob`; eagerly building the full-size data URL here roughly
      // doubled peak memory per photo, which on iOS can tip several large photos
      // in a row into canvas/memory failures. Only uploadImage() reads it.
      get base64() { return canvas.toDataURL(config.format, quality); },
      width,
      height,
      size: blob.size
    };
  }

  // Upload image to server
  static async uploadImage(userId, imageType, file, relatedId = null) {
    try {
      // Compress image first
      const compressed = await this.compressImage(file, imageType);
      
      // Check size limit
      const config = this.IMAGE_CONFIG[imageType];
      if (compressed.size > config.maxSize) {
        throw new Error(`Image size exceeds ${config.maxSize / 1024}KB limit`);
      }
      
      const response = await fetch(`${UserImageService.API_IMAGES}/upload`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          userId,
          imageType,
          relatedId,
          imageData: compressed.base64,
          width: compressed.width,
          height: compressed.height,
          fileSize: compressed.size,
          mimeType: this.IMAGE_CONFIG[imageType].format
        })
      });
      
      if (!response.ok) {
        // The server says why when it can — a profile picture or banner that
        // moderation removed cannot be written over (C18), and "Failed to
        // upload image" told its owner nothing they could act on.
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to upload image');
      }
      
      const result = await response.json();
      return {
        success: true,
        imageId: result.imageId,
        imageUrl: result.imageUrl
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get user's profile picture
  static async getProfilePicture() {
    try {
      if (!AuthService.isAuthenticated()) {
        return null;
      }

      const response = await fetch(`${UserImageService.API_IMAGES}/profile`, {
        headers: AuthService.getAuthHeaders()
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      return result.imageData; // URL string from backend (/api/user-images/:token)
    } catch (error) {
      console.error('Error fetching profile picture:', error);
      return null;
    }
  }

  static async deleteProfilePicture() {
    try {
      const response = await fetch(`${UserImageService.API_IMAGES}/profile`, {
        method: 'DELETE',
        headers: AuthService.getAuthHeaders()
      });
      const result = await response.json();
      return { success: response.ok && result.success === true, error: result.error };
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      return { success: false, error: error.message };
    }
  }

  // Get the current user's profile banner URL (null if none set)
  static async getBanner() {
    try {
      if (!AuthService.isAuthenticated()) {
        return null;
      }
      const response = await fetch(`${UserImageService.API_IMAGES}/banner`, {
        headers: AuthService.getAuthHeaders()
      });
      if (!response.ok) {
        return null;
      }
      const result = await response.json();
      return result.imageData; // URL string from backend (/api/user-images/:token)
    } catch (error) {
      console.error('Error fetching banner:', error);
      return null;
    }
  }

  // Remove the current user's profile banner (singleton)
  static async deleteBanner() {
    try {
      const response = await fetch(`${UserImageService.API_IMAGES}/banner`, {
        method: 'DELETE',
        headers: AuthService.getAuthHeaders()
      });
      // 404 is an empty slot: a retry after a lost response, or a banner
      // moderation took down (the server keeps it but it is not the owner's
      // to show). Either way there is nothing left to remove.
      if (response.status === 404) return { success: true };
      const result = await response.json();
      return { success: !!result.success, error: result.error };
    } catch (error) {
      console.error('Error deleting banner:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete an image
  static async deleteImage(imageId) {
    try {
      const response = await fetch(`${UserImageService.API_IMAGES}/${imageId}`, {
        method: 'DELETE',
        headers: AuthService.getAuthHeaders()
      });
      
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to delete image');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting image:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper: Convert file to Base64
  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Helper: Get image size in readable format
  static formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  // Helper: Validate image file type
  static isValidImageType(file) {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    return validTypes.includes(file.type);
  }

  // Future migration helpers (Phase 2/3)
  static async migrateToFileSystem(imageId) {
    // Placeholder for future migration to file system
    // Will read from SQLite and save to /uploads folder
    console.log('Ready for Phase 2 migration');
  }

  static async migrateToCloudStorage(imageId) {
    // Placeholder for future migration to S3/Cloudinary
    // Will read from file system and upload to cloud
    console.log('Ready for Phase 3 migration');
  }
}

export default UserImageService;